import React from "react";
import SEO from "../components/SEO";
import SoftwareSolutions from "../components/SoftwareSolutions";
import ContactCTA from "../components/ContactCTA";
import SoftwareDevice from "../assets/images/softWareHeroSection.png";
import MonitorIcon from "../assets/svg/monitor.svg";
import MeterIcon from "../assets/svg/meter.svg";
import VernIcon from "../assets/svg/vern.svg";
import ThumbsIcon from "../assets/svg/thumbsUp.svg";
import FAQ from "../components/FAQ";
import SpanaIcon from "../assets/svg/spana.svg";
import UiIcon from "../assets/svg/ui.svg";
import CloudIcon from "../assets/svg/cloud.svg";
import MultiIcon from "../assets/svg/multi-dev.svg";
function SoftwarePage() {
  const faqs = [
    {
      question: "What is the VernPOS POS System?",
      answer:
        "The VernPOS POS System is a complete sales and operations platform designed to handle transactions, manage inventory, track staff activity, and streamline your daily business workflow. It brings all essential functions into one intuitive interface for smooth, efficient operations.",
    },
    {
      question: "How does the POS System support my business workflow?",
      answer:
        "The POS syncs all orders, payments, and stock updates in real time across the VernPOS ecosystem—including KDS and CDS—ensuring accuracy and preventing mismatched information. It reduces manual work, eliminates errors, and keeps your counter operations running flawlessly.",
    },
    {
      question: "Can I customise my menu, products, or pricing?",
      answer:
        "Yes. VernPOS POS allows easy customization of product lists, categories, pricing, modifiers, and promotions. You can tailor the entire menu or catalogue to match your business setup without needing any technical knowledge.",
    },
    {
      question: "What makes VernPOS POS different from other systems?",
      answer:
        "VernPOS offers a clean, responsive interface, real-time multi-device synchronization, and powerful backend reporting tools. The system is optimized for speed and reliability, ensuring smoother checkouts even during peak hours.",
    },
    {
      question: "Is the POS System easy for staff to learn?",
      answer:
        "Absolutely. The interface is designed for fast learning with minimal training required. Staff can quickly navigate the system, reduce errors, and work more efficiently—whether they're new or experienced.",
    },
    {
      question: "What devices and setups do the POS support?",
      answer:
        "VernPOS POS works across a wide range of hardware including tablets, touch-screen terminals, and desktop setups. Multi-counter, multi-branch, and mobile configurations are fully supported for different business needs.",
    },
  ];
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="POS Software Solutions | VernPOS Malaysia"
        description="Discover our comprehensive POS software solutions. Cloud-based, scalable, and designed for retail, F&B, and service businesses across Malaysia."
        keywords="POS software Malaysia, cloud POS system, retail software, F&B software, restaurant POS, inventory management, VernPOS"
      />
      {/* Hero Section */}
      <section className="relative min-h-screen overflow-hidden">
        {/* Two-tone Background */}
        <div className="absolute inset-0">
          {/* Red Background - Top 70% */}
          <div className="absolute top-0 left-0 right-0 h-[70%] bg-gradient-to-br bg-vern to-red-700"></div>
          {/* White Background - Bottom 30% */}
          <div className="absolute bottom-0 left-0 right-0 h-[30%] bg-white"></div>
          {/* Smooth Curved Transition */}
          <div className="absolute left-0 right-0" style={{ top: "100%", height: "10%" }}>
            <svg viewBox="0 0 1200 120" className="w-full h-full" preserveAspectRatio="none">
              <path d="M0,0 C400,80 800,80 1200,0 L1200,120 L0,120 Z" fill="white" />
            </svg>
          </div>
        </div>
        {/* Content Container */}
        <div className="relative z-10 container mx-auto max-w-6xl px-4 pt-32 pb-16">
          {/* Centered Text Content */}
          <div className="text-center mb-36">
            <h1 className="text-5xl md:text-5xl lg:text-5xl font-bold text-white mb-6 leading-tight max-w-4xl mx-auto">
              Smart POS Software to Run Your Business Better
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed max-w-3xl mx-auto">
              VernPOS gives you all the tools you need to manage sales, inventory, staff, and customers — all in one powerful platform.
            </p>
          </div>
          {/* POS Software Image - Centered and Overlapping */}
          <div className="relative flex justify-center">
            <div className="relative z-20 transform hover:scale-105 transition-transform duration-300">
              <img
                src={SoftwareDevice}
                alt="Professional POS Software Interface"
                className="w-full max-w-3xl h-auto object-contain drop-shadow-2xl"
                style={{
                  filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.3)) drop-shadow(0 0 20px rgba(220,38,38,0.2))",
                  marginTop: "-100px", // Overlap with navbar
                }}
              />
            </div>
            {/* Subtle Glow Effect */}
            <div
              className="absolute inset-0 z-10 blur-3xl opacity-30"
              style={{
                background: "radial-gradient(ellipse at center, rgba(220,38,38,0.4) 0%, transparent 70%)",
                transform: "scale(1.2)",
              }}
            ></div>
          </div>
        </div>
        {/* Additional Decorative Elements */}
        <div className="absolute top-1/4 left-10 w-20 h-20 bg-white/10 rounded-full blur-xl"></div>
        <div className="absolute top-1/3 right-16 w-32 h-32 bg-red-400/20 rounded-full blur-2xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-24 h-24 bg-white/5 rounded-full blur-lg"></div>
      </section>
      {/* Built for Efficiency Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-6xl font-bold text-black mb-4">
              Smarter POS.
              <br />
              Smoother Operations.
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              All the essential tools to run your business — fast, flexible and built to grow with you.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
            <div className="flex items-start p-6 bg-[#f6f6f7] rounded-3xl">
              <div className="bg-[#FCF4F3] rounded-full w-16 h-16 flex items-center justify-center mr-4 flex-shrink-0 my-6">
                <img src={MonitorIcon} alt="Monitor Icon" className="w-8 h-8 text-vern" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-black mb-1 my-2">Real-Time Sales Tracking</h3>
                <p className="text-gray-600 font-medium">
                  Monitor your daily, weekly, and monthly sales from anywhere with live data and detailed performance charts.
                </p>
              </div>
            </div>
            <div className="flex items-start p-6 bg-[#f6f6f7] rounded-3xl">
              <div className="bg-[#FCF4F3] rounded-full w-16 h-16 flex items-center justify-center mr-4 flex-shrink-0 my-6">
                <img src={MeterIcon} alt="Performance Icon" className="w-8 h-8 text-vern" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-black mb-2">Inventory Made Easy</h3>
                <p className="text-gray-600 font-medium">
                  Track product quantities with ease, organize your items efficiently, and keep your inventory up to date.
                </p>
              </div>
            </div>
            <div className="flex items-start p-6 bg-[#f6f6f7] rounded-3xl">
              <div className="bg-[#FCF4F3] rounded-full w-16 h-16 flex items-center justify-center mr-4 flex-shrink-0 my-6">
                <img src={VernIcon} alt="VernPOS Icon" className="w-8 h-8 text-vern" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-black mb-2">Detailed Reporting & Insights</h3>
                <p className="text-gray-600 font-medium">
                  Make smarter business decisions using real-time sales trends, product breakdowns, and performance analytics.
                </p>
              </div>
            </div>
            <div className="flex items-start p-6 bg-[#f6f6f7] rounded-3xl">
              <div className="bg-[#FCF4F3] rounded-full w-16 h-16 flex items-center justify-center mr-4 flex-shrink-0 my-6">
                <img src={ThumbsIcon} alt="Thumbs Up Icon" className="w-8 h-8 text-vern" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-black mb-2">Staff & User Management</h3>
                <p className="text-gray-600 font-medium">
                  Assign staff roles, manage users securely, set access permissions, and monitor team performance over time.
                </p>
              </div>
            </div>
          </div>
          <div className="text-center mt-12">
            {/* Google Play Badge */}
            <div className="inline-block">
              <a
                href="https://play.google.com/store/apps/details?id=com.vernpos.posnative"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block transition-transform hover:scale-105"
                aria-label="Download Vern POS on Google Play"
              >
                <img
                  src="https://play.google.com/intl/en_us/badges/images/generic/en_badge_web_generic.png"
                  alt="Get Vern POS on Google Play"
                  className="h-20 w-auto md:h-24"
                />
              </a>
            </div>
            {/* <button className="bg-vern hover:bg-red-700 text-white font-semibold py-3 px-8 rounded-full transition-colors duration-300">
                            Start Free Trial
                        </button> */}
          </div>
        </div>{" "}
      </section>
      {/* Software Solutions */}
      <SoftwareSolutions />
      {/* Designed for Simplicity Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">Designed for Simplicity. Packed with Power</h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Powerful, user-friendly, and built to simplify your daily operations — so you can focus on growing your business.
            </p>
          </div>
          {/* Feature Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Quick & Easy Setup */}
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <img src={SpanaIcon} alt="Quick Setup Icon" className="w-12 h-12 text-vern" />
              </div>
              <h3 className="text-xl font-bold text-black mb-2">Quick & Easy Setup</h3>
              <p className="text-gray-600 text-sm">Get started fast with minimal configuration required.</p>
            </div>
            {/* User-Friendly Interface */}
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <img src={UiIcon} alt="Quick Setup Icon" className="w-12 h-12 text-vern" />
              </div>
              <h3 className="text-xl font-bold text-black mb-2">User-Friendly Interface</h3>
              <p className="text-gray-600 text-sm">Simple and intuitive design, no training required.</p>
            </div>
            {/* Cloud Access Portal */}
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <img src={CloudIcon} alt="Quick Setup Icon" className="w-12 h-12 text-vern" />
              </div>
              <h3 className="text-xl font-bold text-black mb-2">Cloud Access Portal</h3>
              <p className="text-gray-600 text-sm">Manage your business from anywhere, anytime.</p>
            </div>
            {/* Multi-Device Ready */}
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <img src={MultiIcon} alt="Quick Setup Icon" className="w-12 h-12 text-vern" />
              </div>
              <h3 className="text-xl font-bold text-black mb-2">Multi-Device Ready</h3>
              <p className="text-gray-600 text-sm">Use it on tablets, desktops, or POS terminals.</p>
            </div>
          </div>
          {/* Google Play Badge */}
          <div className="text-center">
            <div className="inline-block">
              <a
                href="https://play.google.com/store/apps/details?id=com.vernpos.posnative"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block transition-transform hover:scale-105"
                aria-label="Download Vern POS on Google Play"
              >
                <img
                  src="https://play.google.com/intl/en_us/badges/images/generic/en_badge_web_generic.png"
                  alt="Get Vern POS on Google Play"
                  className="h-20 w-auto md:h-24"
                />
              </a>
            </div>
          </div>
        </div>
      </section>
      {/* FQA  */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <FAQ title="Frequently Asked Questions" faqs={faqs} />
      </div>
      {/* Contact CTA */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <ContactCTA />
      </div>
    </div>
  );
}
export default SoftwarePage;
